import { FC, ReactElement } from 'react';

import { RootState } from 'store/rootReducer';

import { useAppSelector } from 'hooks/defaultStoreHooks';

import { RaaModalPage } from 'raa-react';

import { SpinnerContainer } from 'components/drawerSections/DrawerSections';
import SkeletonImage from 'components/skeleton/SkeletonImage';

import { BildResursData, RitningResursData } from 'models';

import { formatDate } from 'utilities/dateUtilities';
import { isBildResursData, isRitningResursdata } from 'utilities/typeGuards';

export interface FotografiPresentationModalProps {
  resurs?: BildResursData | RitningResursData;
  onClose?: () => void;
}

const DokumentationsresursPresentationModal: FC<FotografiPresentationModalProps> = ({
  resurs,
  onClose
}): ReactElement => {
  const dokumentationlicenstyper = useAppSelector(
    (state: RootState) => state.bebyggelseobjektDomaner.data?.dokumentationlicenstyper
  );

  return (
    <RaaModalPage header={resurs && isRitningResursdata(resurs) ? 'Situationsplan' : 'Fotografi'} close={onClose}>
      <SpinnerContainer showSpinner={!resurs}>
        {resurs && (
          <div className="tw-grid tw-grid-cols-3 tw-border-b tw-border-raa-gray-3 tw-px-4 tw-pb-4 md:tw-px-6 md:tw-pb-6">
            <div className="tw-col-span-3 tw-mb-4 md:tw-col-span-1 md:tw-mb-0">
              <div className="tw-mx-2 tw-mb-4">
                {(resurs.url && (
                  <a href={resurs.url} target="_blank" rel="noreferrer">
                    <SkeletonImage
                      className="tw-w-full tw-object-cover"
                      skeletonClassNames="tw-w-full tw-h-64"
                      src={resurs.url}
                      alt="Vald bild"
                    />
                  </a>
                )) || (
                  <div className="tw-flex tw-h-64 tw-w-full tw-items-center tw-justify-center tw-rounded-md tw-bg-raa-gray-3">
                    <div className="tw-text-center tw-text-raa-gray-6">
                      <span className="raa-icon raa-icon-image tw-block"></span>
                      Ingen bild
                    </div>
                  </div>
                )}
              </div>
            </div>

            <dl className="tw-col-span-3 tw-ml-2 md:tw-col-span-2 md:tw-ml-8 [&>dd]:tw-mb-4 [&>dt]:tw-font-semibold">
              {resurs.titel && (
                <>
                  <dt>Titel</dt>
                  <dd>{resurs.titel}</dd>
                </>
              )}
              {isBildResursData(resurs) && resurs.bildtext && (
                <>
                  <dt>Bildtext</dt>
                  <dd>{resurs.bildtext}</dd>
                </>
              )}
              {isRitningResursdata(resurs) && resurs.motivbeskrivning && (
                <>
                  <dt>Motivbeskrivning</dt>
                  <dd>{resurs.motivbeskrivning}</dd>
                </>
              )}
              <dt>Vy</dt>
              <dd>{(isRitningResursdata(resurs) || isBildResursData(resurs)) && resurs.vy}</dd>
              <dt>Licens</dt>
              <dd>{resurs.licensnamn || dokumentationlicenstyper?.find((l) => l.uri === resurs.licensUri)?.namn}</dd>
              {isBildResursData(resurs) && (
                <>
                  <dt>Fotograf</dt>
                  <dd>{resurs.upphovsman}</dd>
                </>
              )}
              <dt>{isRitningResursdata(resurs) ? 'Tillkomstdatum' : 'Fotografiet taget'}</dt>
              <dd>{formatDate(resurs.tillkomstdatum)}</dd>

              {resurs?.presentationUrl && (
                <p>
                  <a href={resurs.presentationUrl} rel="noreferrer" target="_blank" className="raa-link">
                    <span aria-hidden="true" className={`raa-icon raa-icon-externallink !tw-no-underline`} /> Visa i
                    arkivet
                  </a>
                </p>
              )}
            </dl>
          </div>
        )}
        <div className="tw-mt-6 tw-flex tw-flex-col tw-justify-end tw-px-4 tw-pb-4 md:tw-flex-row md:tw-px-6 md:tw-pb-6">
          <button className="raa-button" type="button" onClick={onClose}>
            Stäng
          </button>
        </div>
      </SpinnerContainer>
    </RaaModalPage>
  );
};

export default DokumentationsresursPresentationModal;
